import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Row, Col, Badge, Spinner, UncontrolledTooltip } from 'reactstrap';
import BZAPIService from '../../services/BZAPIService';
import RunRequirementsView from './source-health/RunRequirementsView';
import moment from 'moment';
import './VendorHealthStatus.css';
import '../bz/runHealth.css';

const STATUS_COLORS = {
  OK: 'success',
  DEGRADED: 'warning',
  UNAVAILABLE: 'danger',
  ERROR: 'danger',
  TIMEOUT: 'secondary',
  UNKNOWN: 'secondary',
  null: 'secondary'
};

const STATUS_MESSAGES = {
  OK: 'All systems operational',
  DEGRADED: 'Service degraded',
  UNAVAILABLE: 'Service unavailable',
  ERROR: 'Service error',
  TIMEOUT: 'Connection timeout',
  UNKNOWN: 'Status unknown',
  null: 'Status unknown'
};

// Source Health -> Vendor status pages: the vendors' public status pages, as
// before STRY0038849. The page head (title, switch, Refresh) lives in
// VendorHealthStatusPage below; this view keeps its filter and cards unchanged.
const VendorStatusPagesView = ({ isCompactView = false, reloadToken = 0 }) => {
  const [loading, setLoading] = useState(true);
  const [vendorStatusData, setVendorStatusData] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [availableStatuses, setAvailableStatuses] = useState(['ALL']);

  const filteredVendors = useMemo(() => {
    if (!vendorStatusData.length) return [];
    return statusFilter === 'ALL'
      ? vendorStatusData
      : vendorStatusData.filter(vendor => vendor.currentStatus?.status === statusFilter);
  }, [statusFilter, vendorStatusData]);

  useEffect(() => {
    loadVendorHealthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadToken]);

  const loadVendorHealthStatus = async () => {
    try {
      setLoading(true);
      const response = await BZAPIService.getVendorHealthStatus();
      const vendors = response.vendors || [];
      setVendorStatusData(vendors);

      // Extract unique statuses from the vendor data
      const uniqueStatuses = new Set(['ALL']);
      vendors.forEach(vendor => {
        if (vendor.currentStatus && vendor.currentStatus.status) {
          uniqueStatuses.add(vendor.currentStatus.status);
        }
      });
      setAvailableStatuses(Array.from(uniqueStatuses));

      setLoading(false);
    } catch (error) {
      console.error('Failed to load vendor health status:', error);
      setLoading(false);
    }
  };

  const renderTimelinePoint = (entry, vendorId, idx) => {
    const pointId = `timeline-${vendorId}-${idx}`;

    return (
      <>
        <div
          id={pointId}
          className="timeline-point"
          style={{
            backgroundColor: entry.color,
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            display: 'inline-block',
            margin: '0 3px',
            cursor: 'pointer',
            border: '1px solid rgba(0,0,0,0.1)'
          }}
        />
        <UncontrolledTooltip placement="top" target={pointId}>
          <div style={{ textAlign: 'left' }}>
            <div>
              <strong>Date:</strong> {moment(entry.date).format('MMM D, YYYY')}
            </div>
            <div>
              <strong>Status:</strong> {entry.scoreLabel}
            </div>
            <div>
              <strong>Score:</strong> {entry.score !== null ? (entry.score * 100).toFixed(1) + '%' : 'N/A'}
            </div>
            <div>
              <strong>Coverage:</strong> {(entry.coveragePct * 100).toFixed(1)}%
            </div>
            {entry.transitions > 0 && (
              <div>
                <strong>Transitions:</strong> {entry.transitions}
              </div>
            )}
          </div>
        </UncontrolledTooltip>
      </>
    );
  };

  const renderTimeline = vendor => {
    if (!vendor.history || vendor.history.length === 0) {
      return <span className="text-muted">No historical data available</span>;
    }

    // History is already sorted by date (newest first) from the API
    // Display as-is: newest on left, oldest on right
    const sortedHistory = [...vendor.history];

    return (
      <div className="d-flex align-items-center timeline-container">
        <span className="text-muted small mr-2">{moment(sortedHistory[0].date).format('MMM D')}</span>
        {sortedHistory.map((entry, idx) => (
          <React.Fragment key={idx}>{renderTimelinePoint(entry, vendor.vendorId, idx)}</React.Fragment>
        ))}
        <span className="text-muted small ml-2">
          {moment(sortedHistory[sortedHistory.length - 1].date).format('MMM D')}
        </span>
      </div>
    );
  };

  const handleFilterChange = filter => {
    setStatusFilter(filter);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="vendor-health-status-container">
      {!isCompactView && (
        <>
          <div className="mb-3">
            <div className="d-flex align-items-center mb-3">
              <div className="mr-2">Filter by status:</div>
              {availableStatuses.map(status => {
                const isActive = statusFilter === status;
                const STATUS_LABELS = {
                  ALL: { label: 'All', activeColor: 'primary' },
                  OK: { label: 'Operational', activeColor: 'success' },
                  DEGRADED: { label: 'Degraded', activeColor: 'warning' },
                  ERROR: { label: 'Error', activeColor: 'danger' },
                  UNAVAILABLE: { label: 'Unavailable', activeColor: 'danger' },
                  TIMEOUT: { label: 'Timeout', activeColor: 'secondary' },
                  UNKNOWN: { label: 'Unknown', activeColor: 'secondary' }
                };
                const statusConfig = STATUS_LABELS[status] || { label: status, activeColor: 'light' };
                const color = isActive ? statusConfig.activeColor : 'light';
                const label = statusConfig.label;

                return (
                  <Badge
                    key={status}
                    color={color}
                    className={`mr-2 cursor-pointer ${!isActive ? 'text-dark' : ''}`}
                    onClick={() => handleFilterChange(status)}
                    pill
                  >
                    {label}
                  </Badge>
                );
              })}
            </div>
          </div>
        </>
      )}

      {filteredVendors.length === 0 ? (
        <Card className="mb-3 bg-transparent border-0">
          <CardBody>
            <div className="text-center">No vendors match the selected filter</div>
          </CardBody>
        </Card>
      ) : isCompactView ? (
        // Compact view for dashboard display

        <Row>
          {filteredVendors.slice(0, 6).map((vendor, index) => (
            <Col key={vendor.vendorId} md={4} className="mb-3">
              <Card className="h-100 bg-transparent">
                <CardBody className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">{vendor.vendorName}</h6>
                    {vendor.currentStatus ? (
                      <Badge color={STATUS_COLORS[vendor.currentStatus.status]}>{vendor.currentStatus.status}</Badge>
                    ) : (
                      <Badge color={STATUS_COLORS.UNKNOWN}>UNKNOWN</Badge>
                    )}
                  </div>
                  <div className="small mb-2">
                    {vendor.currentStatus
                      ? vendor.currentStatus.message || STATUS_MESSAGES[vendor.currentStatus.status]
                      : STATUS_MESSAGES.UNKNOWN}
                  </div>
                  <div className="small text-muted">Past 7 days:</div>
                  {renderTimeline(vendor)}
                </CardBody>
              </Card>
            </Col>
          ))}
          {filteredVendors.length > 6 && (
            <Col md={12}>
              <div className="text-center text-muted small">
                {filteredVendors.length - 6} more vendor(s) not shown in compact view
              </div>
            </Col>
          )}
        </Row>
      ) : (
        // Full view
        filteredVendors.map((vendor, index) => (
          <Card key={vendor.vendorId} className="mb-3">
            <CardBody>
              <Row>
                <Col md={3}>
                  <h5 className="mb-0">{vendor.vendorName}</h5>
                  <div className="text-muted small">
                    {vendor.currentStatus
                      ? moment(vendor.currentStatus.timestamp).format('MMM D, HH:mm')
                      : 'No data available'}
                  </div>
                </Col>
                <Col md={2}>
                  {vendor.currentStatus ? (
                    <Badge color={STATUS_COLORS[vendor.currentStatus.status]}>{vendor.currentStatus.status}</Badge>
                  ) : (
                    <Badge color={STATUS_COLORS.UNKNOWN}>UNKNOWN</Badge>
                  )}
                </Col>
                <Col md={4}>
                  {vendor.currentStatus
                    ? vendor.currentStatus.message || STATUS_MESSAGES[vendor.currentStatus.status]
                    : STATUS_MESSAGES.UNKNOWN}
                </Col>
                <Col md={3}>
                  <div className="small text-muted mb-1">Past 7 days:</div>
                  {renderTimeline(vendor)}
                </Col>
              </Row>
            </CardBody>
          </Card>
        ))
      )}
    </div>
  );
};

VendorStatusPagesView.propTypes = {
  isCompactView: PropTypes.bool,
  reloadToken: PropTypes.number
};

const VIEWS = [
  {
    id: 'requirements',
    label: 'Run requirements',
    subtitle: "What each vendor's bug run needs, checked at the start of every run."
  },
  {
    id: 'status-pages',
    label: 'Vendor status pages',
    subtitle: 'Health of the upstream sources Prevent and Predict depend on'
  }
];

const tabId = id => `source-health-tab-${id}`;
const PANEL_ID = 'source-health-panel';

// Source Health (platform/source-health): a "Run requirements | Vendor status
// pages" switch, Run requirements first (STRY0038849).
const VendorHealthStatusPage = ({ isCompactView = false }) => {
  const [view, setView] = useState(VIEWS[0].id);
  const [reloadToken, setReloadToken] = useState(0);
  const tabRefs = useRef({});

  if (isCompactView) return <VendorStatusPagesView isCompactView />;

  const current = VIEWS.find(v => v.id === view);

  // Tabs pattern: arrow keys move between the two views.
  const onTabKeyDown = event => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const index = VIEWS.findIndex(v => v.id === view);
    const next = VIEWS[(index + (event.key === 'ArrowRight' ? 1 : VIEWS.length - 1)) % VIEWS.length];
    setView(next.id);
    tabRefs.current[next.id]?.focus();
  };

  return (
    <div style={{ maxWidth: 1320, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div className="bz-section-head" style={{ marginBottom: 0, flexWrap: 'wrap' }}>
        <div>
          <div className="bz-kicker" style={{ color: 'var(--plan)' }}>
            Platform · Operations
          </div>
          <h1 className="bz-h1" style={{ marginTop: 4 }}>
            Source Health
          </h1>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
            {current.subtitle}
          </div>
        </div>
        <div className="bz-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <div className="rh-seg" role="tablist" aria-label="Source Health views">
            {VIEWS.map(v => (
              <button
                key={v.id}
                ref={el => {
                  tabRefs.current[v.id] = el;
                }}
                type="button"
                role="tab"
                id={tabId(v.id)}
                aria-selected={view === v.id}
                aria-controls={PANEL_ID}
                tabIndex={view === v.id ? 0 : -1}
                className={view === v.id ? 'is-on' : ''}
                onClick={() => setView(v.id)}
                onKeyDown={onTabKeyDown}
              >
                {v.label}
              </button>
            ))}
          </div>
          <button type="button" className="bz-btn bz-btn--sm" onClick={() => setReloadToken(t => t + 1)}>
            Refresh
          </button>
        </div>
      </div>

      <div
        id={PANEL_ID}
        role="tabpanel"
        aria-labelledby={tabId(view)}
        style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
      >
        {view === 'requirements' ? (
          <RunRequirementsView reloadToken={reloadToken} />
        ) : (
          <VendorStatusPagesView reloadToken={reloadToken} />
        )}
      </div>
    </div>
  );
};

VendorHealthStatusPage.propTypes = {
  isCompactView: PropTypes.bool
};

export default VendorHealthStatusPage;
